/**
 * Pure helpers for the OpenSeaMap seamark tile cache — the bits with no Capacitor
 * or MapLibre dependency, so the URL parsing and the LRU eviction policy are
 * unit-tested without a device or a map. The native glue lives in
 * seamarkTileCache.ts and reuses these.
 */

/** MapLibre custom-protocol scheme for cached seamark tiles: seamarkcache://z/x/y.png */
export const SEAMARK_PROTOCOL = 'seamarkcache';

/** Cap the on-disk seamark cache. Transparent PNGs are tiny, so this is thousands. */
export const CAP_BYTES = 40 * 1024 * 1024;
/** Evict down to this once the cap is exceeded, so we don't thrash on every write. */
export const LOW_WATER = 32 * 1024 * 1024;

/** Parse a seamarkcache://z/x/y.png URL into its tile coords, or null if malformed. */
export function parseSeamarkTileUrl(url: string): { z: string; x: string; y: string } | null {
  const m = /:\/\/(\d+)\/(\d+)\/(\d+)(?:\.png)?/.exec(url);
  return m ? { z: m[1], x: m[2], y: m[3] } : null;
}

/** Flat on-disk file name for a tile (flat keeps readdir + eviction simple). */
export function tileName(z: string, x: string, y: string): string {
  return `${z}_${x}_${y}.png`;
}

/**
 * Given the LRU (oldest-first: filename -> byte size) and the current total, decide
 * which files to evict to get back under lowWater once total exceeds cap. Returns
 * the names to delete and the bytes freed. Pure, so the policy is testable.
 */
export function selectEvictions(
  lru: Map<string, number>,
  total: number,
  cap: number,
  lowWater: number,
): { names: string[]; freed: number } {
  if (total <= cap) return { names: [], freed: 0 };
  const names: string[] = [];
  let freed = 0;
  for (const [name, size] of lru) {
    if (total - freed <= lowWater) break;
    names.push(name);
    freed += size;
  }
  return { names, freed };
}
